import { Box, useColorModeValue } from '@chakra-ui/react';
import ReactApexChart from 'react-apexcharts';
import { useMemo } from 'react';

interface SparklineProps {
	data: number[];
	color?: string;
	width?: number;
	height?: number;
}

/**
 * Tiny inline trend chart (40-50px) for KPI stat cards.
 * Shows the trend at a glance without axis labels or tooltips.
 */
export default function Sparkline({
	data,
	color,
	width = 80,
	height = 36,
}: SparklineProps) {
	const defaultColor = useColorModeValue('#023F7F', '#6D9FD3');
	const chartColor = color ?? defaultColor;

	const options: ApexCharts.ApexOptions = useMemo(() => ({
		chart: {
			type: 'area',
			sparkline: { enabled: true },
			animations: { enabled: false },
		},
		stroke: {
			curve: 'smooth',
			width: 2,
		},
		fill: {
			type: 'gradient',
			gradient: {
				shadeIntensity: 1,
				opacityFrom: 0.3,
				opacityTo: 0.01,
				stops: [0, 100],
			},
		},
		colors: [chartColor],
		tooltip: { enabled: false },
		xaxis: { labels: { show: false } },
		yaxis: { labels: { show: false } },
	}), [chartColor]);

	const series = useMemo(() => [{ data }], [data]);

	if (!data || data.length < 2) return null;

	return (
		<Box w={`${width}px`} h={`${height}px`} opacity={0.9}>
			<ReactApexChart
				options={options}
				series={series}
				type='area'
				width={width}
				height={height}
			/>
		</Box>
	);
}
